import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../js/api/messaging";
import { buildThreadItems, isOfficeTransaction } from "../../js/api/messaging/helpers";
import { useAuth } from "../../js/hooks/useAuth";
import { useBrokerDesk } from "../../js/hooks/useBrokerDesk";
import { useNotifications } from "../../js/hooks/useNotifications";
import { prettyValue } from "../../js/utils/formatters";
import { isOfficeMessagesPath, messagesPath } from "../../js/utils/messagingPaths";
import { findProspectByTransaction } from "../../js/prospects";
import { useDesk } from "../../js/hooks/useDesk";
import { filterByBrokerDesk, BROKER_DESK } from "../../js/api/brokerDesk";
import { putBlob } from "../../js/db";
import { compressImageFile } from "../../js/utils/imageCompress";
import InquiryComposer from "../desk/InquiryComposer";
import AdminMessageTools from "../../pages/admin/AdminMessageTools";
import ArchiveBadge from "./ArchiveBadge";
import CloseFileDialog from "./CloseFileDialog";
import EmailFlagsNote from "./EmailFlagsNote";
import ExportButton from "./ExportButton";
import MessageComposer from "./MessageComposer";
import MessageThreadList from "./MessageThreadList";
import MessageThreadView from "./MessageThreadView";

const COPY = {
  agent: {
    title: "Client Messaging",
    lead: "A quiet desk for notes with the clients assigned to you. Buyer files and seller files stay on their own journeys. Colleague notes live in Office Messaging.",
  },
  broker: {
    title: "Client Messaging",
    lead: "Client conversations for this brokerage, kept apart from in-office notes. Agents still only see their own assignments.",
  },
  client: {
    title: "Messages with Your Agent",
    lead: "Only your own file appears here — a place to think together, not a rush.",
  },
  admin: {
    title: "Messages",
    lead: "Every client file in the prototype, for development context. Broker notes sit below the inbox. This is not a client-facing view.",
  },
  officeAgent: {
    title: "Office Messaging",
    lead: "Notes with colleagues — other agents and your broker. Client files stay in Client Messaging.",
  },
  officeBroker: {
    title: "Office Messaging",
    lead: "Agent-to-agent and broker-to-agent notes for this office. Client files stay in Client Messaging.",
  },
};

function clientCopy(deskRole, mode) {
  if (deskRole === "broker") {
    return mode === BROKER_DESK.mine
      ? {
          title: "Client Messaging",
          lead: "Only client threads where you are the agent of record. All Agents holds the rest of the office’s client files. Colleague notes live in Office Messaging.",
        }
      : {
          title: "Client Messaging",
          lead: "Client files for this brokerage. Your personal book is on My Business. Colleague notes live in Office Messaging.",
        };
  }
  return COPY[deskRole] || COPY.client;
}

export default function MessagesDesk({ deskRole = "client", archive = false, channel, children }) {
  const { session } = useAuth();
  const desk = useDesk();
  const { mode } = useBrokerDesk();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const archiveView = archive || pathname.endsWith("/archive");
  const office = channel === "office" || (channel !== "client" && isOfficeMessagesPath(pathname));
  const { transactions: liveRows, refresh, error: liveError } = useNotifications();
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeBusy, setCloseBusy] = useState(false);
  const copy = office
    ? deskRole === "broker"
      ? COPY.officeBroker
      : COPY.officeAgent
    : clientCopy(deskRole, mode);
  const canManage = deskRole === "agent" || deskRole === "broker" || deskRole === "admin";
  const pathOpts = { office };
  const inboxPath = messagesPath(deskRole, pathOpts);
  const archivePath = messagesPath(deskRole, { ...pathOpts, archive: true });

  const transactions = useMemo(() => {
    const byChannel = (rows) =>
      office ? rows.filter(isOfficeTransaction) : rows.filter((item) => !isOfficeTransaction(item));
    if (deskRole === "client") {
      return byChannel(liveRows.filter((item) => item.client_id === session?.userId));
    }
    if (deskRole === "broker" && !office) {
      return byChannel(filterByBrokerDesk(liveRows, session, mode));
    }
    return byChannel(liveRows);
  }, [liveRows, deskRole, session, mode, office]);

  useEffect(() => {
    if (liveError) setError(liveError);
    else setError("");
  }, [liveError]);

  const load = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const items = useMemo(() => {
    const all = buildThreadItems(transactions, session?.userId);
    return all.filter((item) => (archiveView ? item.isArchived : !item.isArchived));
  }, [transactions, session?.userId, archiveView]);

  const selectedKey = searchParams.get("thread") || (searchParams.get("tx") ? `tx-${searchParams.get("tx")}` : "");
  const selected =
    items.find((item) => item.key === selectedKey) ||
    items.find((item) => item.threadId === searchParams.get("thread")) ||
    items[0] ||
    null;

  useEffect(() => {
    if (!selected?.threadId || !selected.unread) return;
    api("POST", `/api/messages/thread/${selected.threadId}/read`, {}, session).then(load);
  }, [selected?.threadId, selected?.unread, session, load]);

  function selectItem(item) {
    setNotice("");
    setSearchParams(
      item.threadId ? { thread: item.threadId } : { tx: item.transactionId },
      { replace: true }
    );
  }

  async function handleSend(text, files = [], tag = null) {
    if (!selected?.threadId) return;
    let attachments = [];
    try {
      attachments = await Promise.all(
        files.map(
          (file) =>
            new Promise((resolve, reject) => {
              const isPhoto = String(file.type || "").startsWith("image/");
              if (!isPhoto && file.size > 2 * 1024 * 1024) {
                reject(new Error("Please keep documents under 2 MB. Photos may be up to 5 MB."));
                return;
              }
              Promise.resolve(isPhoto ? compressImageFile(file) : file)
                .then((ready) => {
                  const reader = new FileReader();
                  reader.onerror = () => reject(new Error("That file could not be read."));
                  reader.onload = () => {
                    const path = putBlob(`local://messages/${crypto.randomUUID()}/${ready.name}`, reader.result);
                    resolve({
                      file_name: ready.name,
                      mime_type: ready.type || "application/octet-stream",
                      file_path: path,
                    });
                  };
                  reader.readAsDataURL(ready);
                })
                .catch(reject);
            })
        )
      );
    } catch (error) {
      setNotice(error.message);
      return;
    }
    const result = await api(
      "POST",
      "/api/messages/send",
      { thread_id: selected.threadId, message_text: text, attachments, tag },
      session
    );
    if (!result.ok) {
      setNotice(result.error || "The note could not be sent.");
      return;
    }
    setNotice("");
    await load();
  }

  async function startThread() {
    if (!selected?.transactionId) return;
    const result = await api(
      "POST",
      "/api/threads/create",
      { transaction_id: selected.transactionId },
      session
    );
    if (!result.ok) {
      setNotice(result.error || "Could not open a thread.");
      return;
    }
    await load();
    setSearchParams({ thread: result.data.id }, { replace: true });
  }

  async function setThreadAside() {
    if (!selected?.threadId) return;
    const result = await api("POST", "/api/threads/archive", { thread_id: selected.threadId }, session);
    if (!result.ok) {
      setNotice(result.error || "Could not set this thread aside.");
      return;
    }
    navigate(messagesPath(deskRole, { ...pathOpts, archive: true, threadId: selected.threadId }));
  }

  async function closeFile(status) {
    const transactionId = selected?.transactionId;
    if (!transactionId) return;
    setCloseBusy(true);
    const result = await api("POST", `/api/transactions/${transactionId}/archive`, { status }, session);
    setCloseBusy(false);
    if (!result.ok) {
      setNotice(result.error || "Could not archive this file.");
      return;
    }
    setCloseOpen(false);
    await load();
    const flags = result.data?.email;
    const queued = flags
      ? [flags.broker?.send && "broker", flags.agent?.send && "agent", flags.client?.send && "client"]
          .filter(Boolean)
          .join(", ")
      : "";
    if (result.data?.idempotent) {
      setNotice("This file was already archived. Nothing was duplicated.");
    } else {
      setNotice(
        queued
          ? `File archived. A communication report was saved. Email queued for the ${queued}.`
          : "File archived. A communication report was saved."
      );
    }
    navigate(
      messagesPath(deskRole, {
        ...pathOpts,
        archive: true,
        threadId: selected.threadId,
        transactionId: selected.threadId ? undefined : transactionId,
      })
    );
  }

  const locked =
    !selected?.thread ||
    selected.thread.status === "archived" ||
    selected.transaction?.status !== "open";
  const lockedReason = !selected
    ? ""
    : selected.transaction?.status !== "open"
      ? "This file is archived. You can still read the conversation."
      : selected.thread?.status === "archived"
        ? "This thread was set aside. Start from the inbox if you need a new note."
        : "Open a thread to write a note.";

  const selectedMessages = selected?.thread?.messages || [];
  const prospectCard =
    selected?.clientRole === "prospect" ? findProspectByTransaction(selected.transactionId, session) : null;
  const counterpartLabel = office
    ? [selected?.clientName, selected?.agentName && `Agent ${selected.agentName}`].filter(Boolean).join(" · ") ||
      "Colleague"
    : `${selected?.clientName || "Client"}${
        selected?.clientRole ? ` · ${prettyValue(selected.clientRole)}` : ""
      }${selected?.agentName ? ` · Agent ${selected.agentName}` : ""}`;

  return (
    <div className={`messages-desk${office ? " messages-desk--office" : ""}`} data-tour="messages-desk">
      <header className="messages-desk__intro">
        <h1>{archiveView ? "Archive" : copy.title}</h1>
        <p className="page-lead">
          {archiveView
            ? office
              ? "Closed office notes and threads set aside live here. Nothing is deleted."
              : "Closed files and threads set aside live here. Nothing is deleted."
            : copy.lead}
        </p>
        {canManage && deskRole !== "admin" ? (
          <p className="muted">
            <NavLink to={deskRole === "broker" ? "/broker/audit" : "/agent/audit"}>
              Retention log
            </NavLink>
          </p>
        ) : null}
        <nav className="messages-desk__tabs" aria-label="Inbox or archive">
          <NavLink to={inboxPath} end className={({ isActive }) => `messages-tab${isActive ? " is-active" : ""}`}>
            Inbox
          </NavLink>
          <NavLink to={archivePath} className={({ isActive }) => `messages-tab${isActive ? " is-active" : ""}`}>
            Archive
          </NavLink>
        </nav>
        {office && canManage && !archiveView ? (
          <div className="messages-desk__office-compose card">
            <InquiryComposer desk={deskRole === "broker" ? "broker" : "agent"} />
          </div>
        ) : null}
      </header>
      {deskRole === "admin" && !archiveView ? (
        <AdminMessageTools selected={selected} locked={locked} onSend={handleSend} onNotice={setNotice} />
      ) : null}

      {error ? <p className="field-error">{error}</p> : null}
      {notice ? <p className="confirm-banner">{notice}</p> : null}

      <div className="messages-desk__panes" data-tour="messages-panes">
        <aside className="messages-desk__list">
          <h2 className="messages-desk__pane-title">{archiveView ? "Archived" : office ? "Office notes" : "Conversations"}</h2>
          <MessageThreadList items={items} selectedKey={selected?.key} onSelect={selectItem} />
        </aside>
        <section className="messages-desk__thread" aria-live="polite">
          {selected ? (
            <>
              <header className="messages-desk__thread-head">
                <div>
                  <h2>{selected.title}</h2>
                  <p className="muted">{counterpartLabel}</p>
                  {prospectCard ? (
                    <p>
                      <NavLink to={`${desk.prospects}/${prospectCard.id}`}>Open prospect card</NavLink>
                    </p>
                  ) : null}
                </div>
                <div className="messages-desk__actions">
                  <ArchiveBadge transaction={selected.transaction} thread={selected.thread} />
                  <ExportButton transaction={selected.transaction} onNotice={setNotice} />
                  {canManage && selected.transaction?.status === "open" ? (
                    <button type="button" className="ghost-btn" onClick={() => setCloseOpen(true)}>
                      Close file
                    </button>
                  ) : null}
                  {canManage && selected.thread && selected.thread.status !== "archived" && selected.transaction?.status === "open" ? (
                    <button type="button" className="ghost-btn" onClick={setThreadAside}>
                      Set thread aside
                    </button>
                  ) : null}
                </div>
              </header>
              <EmailFlagsNote flags={selected.transaction?.archives?.[0]?.email} />
              {selected.kind === "empty-tx" ? (
                <div className="messages-desk__empty-thread">
                  <p className="muted">This file does not have a conversation yet.</p>
                  {canManage ? (
                    <button type="button" className="primary-btn" onClick={startThread}>
                      Open a thread
                    </button>
                  ) : (
                    <p className="field-hint">Your agent will open the first note when you are both ready.</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="message-thread messages-desk__thread-body">
                    <MessageThreadView messages={selectedMessages} currentUserId={session?.userId} />
                    <MessageComposer
                      disabled={locked}
                      lockedReason={lockedReason}
                      onSend={handleSend}
                      allowLink={deskRole === "admin"}
                      showBetaTag={Boolean(session?.betaTester)}
                      defaultBetaTag={Boolean(session?.betaTester)}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="messages-desk__empty-thread">
              <p className="muted">
                {archiveView
                  ? "Nothing has been archived yet. Open conversations stay in the inbox."
                  : office
                    ? "Choose a colleague above to start an office note. It will appear here."
                    : "When a conversation begins, it will appear here."}
              </p>
            </div>
          )}
        </section>
      </div>
      {children}
      <CloseFileDialog
        transaction={selected?.transaction}
        open={closeOpen}
        busy={closeBusy}
        onCancel={() => setCloseOpen(false)}
        onConfirm={closeFile}
      />
    </div>
  );
}
